import { z } from "zod";
import { baseEntitySchema } from "./base";

// An outgoing Mercury Bank payment pulled in by the read-only sync, held in an
// inbox to be reconciled against a purchase order. "suggested" = the sync found
// a likely PO by counterparty + amount; "matched" = an admin confirmed (or
// manually picked) the PO; "rejected" = an admin dismissed it; "unmatched" = no
// suggestion.
export const mercuryPaymentMatchStatusSchema = z.enum([
  "unmatched",
  "suggested",
  "matched",
  "rejected",
]);
export type MercuryPaymentMatchStatus = z.infer<
  typeof mercuryPaymentMatchStatusSchema
>;

export const mercuryPaymentDataSchema = z.object({
  transactionId: z.string(),
  accountId: z.string(),
  accountName: z.string().optional(),
  amount: z.number().nonnegative(),
  currency: z.string().default("USD"),
  feeValue: z.number().nonnegative().default(0),
  // "outgoing" = money we sent; "incoming" = money received. The sync currently
  // ingests only outgoing; stored for forward-compatible filtering.
  direction: z.enum(["outgoing", "incoming"]).optional(),
  counterpartyId: z.string().optional(),
  counterpartyName: z.string().optional(),
  counterpartyNickname: z.string().optional(),
  paidAt: z.date().optional(),
  kind: z.string().optional(),
  mercuryStatus: z.string().optional(),
  note: z.string().optional(),
  externalMemo: z.string().optional(),
  bankDescription: z.string().optional(),
  // PayPal pulling funds from Mercury for a PayPal payout — a mirror of a
  // PayPal inbox row, hidden from the inbox.
  paypalFunding: z.boolean().optional(),
  matchStatus: mercuryPaymentMatchStatusSchema,
  // Outgoing → purchase order; incoming → sales order's invoice (direction picks).
  suggestedPoId: z.string().optional(),
  matchedPoId: z.string().optional(),
  suggestedInvoiceId: z.string().optional(),
  matchedInvoiceId: z.string().optional(),
  suggestedSoId: z.string().optional(),
  matchedSoId: z.string().optional(),
  suggestionMismatchPct: z.number().optional(),
  matchedPaymentId: z.string().optional(),
  matchedAt: z.date().optional(),
  matchedByUserId: z.string().optional(),
});

export type MercuryPaymentData = z.infer<typeof mercuryPaymentDataSchema>;
export const mercuryPaymentSchema = baseEntitySchema.merge(
  mercuryPaymentDataSchema,
);
export type MercuryPayment = z.infer<typeof mercuryPaymentSchema>;
